import SnoozeMenu from "../../ui/SnoozeMenu";
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Device } from "../../lib/supabase";
import { ago, fijiTime } from "../../lib/format";
import { orgApi, type AlertEvent } from "../../lib/org";

/** Events tab: latest alerts for the devices this user can see, live, with acknowledge. */
export function useAlertEvents() {
  const [events, setEvents] = useState<AlertEvent[]>([]);
  useEffect(() => {
    // V3.5.0: email-only alerts (inapp = false) are not shown here
    supabase.from("alert_events").select("*").eq("inapp", true).order("at", { ascending: false }).limit(200)
      .then(({ data }) => setEvents((data ?? []) as AlertEvent[]));
    const ch = supabase.channel("alert_events_live")
      .on("postgres_changes", { event: "*", schema: "public", table: "alert_events" }, (p) => {
        const e = p.new as AlertEvent;
        if (!e?.id || e.inapp === false) return;
        setEvents((prev) => [e, ...prev.filter((x) => x.id !== e.id)].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 200));
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);
  return [events, setEvents] as const;
}

export default function EventsPanel({ events, setEvents, devices, onFocus, onAlerts, now }: {
  events: AlertEvent[]; setEvents: (f: (e: AlertEvent[]) => AlertEvent[]) => void; devices: Device[]; onFocus: (deviceId: string, lat?: number | null, lng?: number | null) => void;
  onAlerts: () => void; now: number;
}) {
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const shown = events.filter((e) => !onlyOpen || !e.ack_at);
  const open = events.filter((e) => !e.ack_at).length;
  const name = (id: string) => devices.find((d) => d.id === id)?.name ?? "—";

  async function ack(e: AlertEvent) {
    try { await orgApi("POST", `alert-events/${e.id}/ack`, {}); setEvents((p) => p.map((x) => (x.id === e.id ? { ...x, ack_at: new Date().toISOString() } : x))); }
    catch (x: any) { setErr(x.message); }
  }
  async function ackAll() {
    try { await orgApi("POST", "alert-events/ack-all", {}); const t = new Date().toISOString(); setEvents((p) => p.map((x) => (x.ack_at ? x : { ...x, ack_at: t }))); }
    catch (x: any) { setErr(x.message); }
  }

  return (
    <>
      <div className="ev-head">
        <label className="acheck"><input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Only new ({open})</label>
        <span className="ev-actions">
          <SnoozeMenu />
          {open > 0 && <button className="btn-ghost small" onClick={ackAll}>Mark all seen</button>}
          <button className="btn-ghost small ev-alerts" onClick={onAlerts} title="See all saved alerts or create a new one">🔔 Alerts</button>
        </span>
      </div>
      {err && <div className="error pad">{err}</div>}
      <ul className="list events">
        {shown.map((e) => (
          <li key={e.id} className={e.ack_at ? "seen" : "new"} onClick={() => onFocus(e.device_id, e.lat, e.lng)}>
            <div className="row1">
              <i className={`evdot ev-${e.type}`} />
              <span className="name">{e.message}</span>
            </div>
            <div className="row2">
              {name(e.device_id)} · {fijiTime(e.at)} · {ago(e.at, now)}
              {e.notify_error && <span className="warn-text" title={e.notify_error}> · email failed</span>}
              {!e.ack_at && <button className="btn-ghost small" onClick={(x) => { x.stopPropagation(); ack(e); }}>Seen</button>}
            </div>
          </li>
        ))}
        {!shown.length && <li className="empty muted">No events yet. Every company starts with zone entry/exit and speeding over 85 km/h — open Alerts to change them or add more.</li>}
      </ul>
    </>
  );
}
